import { useMemo } from 'react';
import {
  ButtonRouterLink,
  Card,
  Eyebrow,
  SectionCrosshairs
} from '../components/primitives';
import { detectPlatform } from '../lib/ideDownload';
import { LandingIdeMock, LandingRulesMock, LandingWebMock } from './LandingHeroMocks';

function HeroStaticImage({ figKey, alt }: { figKey: 'fig01' | 'fig02' | 'fig03'; alt: string }) {
  return (
    <>
      <img
        className="lp-surface-static lp-surface-static-light"
        src={`${import.meta.env.BASE_URL}landing-hero/${figKey}-light.png`}
        alt={alt}
        aria-hidden="true"
      />
      <img
        className="lp-surface-static lp-surface-static-dark"
        src={`${import.meta.env.BASE_URL}landing-hero/${figKey}-dark.png`}
        alt={alt}
        aria-hidden="true"
      />
    </>
  );
}

export function Hero() {
  const downloadLabel = useMemo(() => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const platform = detectPlatform(ua);
    if (platform === 'mac') return 'Download for macOS';
    if (platform === 'win') return 'Download for Windows';
    return 'Download IDE';
  }, []);

  return (
    <section className="lp-hero">
      <div className="pn-page">
        <SectionCrosshairs />

        <Eyebrow>One workspace for your team and their agents · OPS.01</Eyebrow>

        <h1 className="lp-hero-h1">
          Organization-Level Agent Harness
        </h1>

        <p className="lp-lede">
          Org-level agentic workflows and rules that are agent and repo agnostic + AI-native
          documentation and agile task management
        </p>

        <div className="lp-ctas">
          <ButtonRouterLink to="/sign-up" variant="primary" size="lg">
            Start free
          </ButtonRouterLink>
          <ButtonRouterLink to="/download" size="lg" data-testid="lp-download-cta">
            {downloadLabel}
          </ButtonRouterLink>
        </div>

        <div className="lp-trust">
          <span className="lp-trust-label">
            <span className="lp-trust-dot" />
            Bring your coding agent
          </span>
          <span className="lp-trust-agents">
            <span>Claude Code</span>
            <span>Codex</span>
            <span>Gemini</span>
            <span>Cline</span>
            <span>Continue</span>
            <span>Copilot</span>
          </span>
        </div>

        <div className="lp-surfaces" aria-label="Product surfaces, one workspace">
          <Card withCorners tag="FIG.01 · Penrose IDE" tagAccent>
            <div className="lp-surface-body lp-surface-live lp-ide-chrome-wrap lp-ide-mock-wrap lp-hero-mock-body">
              <LandingIdeMock />
            </div>
            <HeroStaticImage figKey="fig01" alt="Penrose IDE — documents view" />
          </Card>

          <Card withCorners tag="FIG.02 · Penrose web">
            <div className="lp-surface-body lp-surface-live lp-ide-chrome-wrap lp-web-mock-wrap lp-hero-mock-body">
              <LandingWebMock />
            </div>
            <HeroStaticImage figKey="fig02" alt="Penrose web — work items board" />
          </Card>

          <Card withCorners tag="FIG.03 · Agent rules per role" tagAccent>
            <div className="lp-surface-body lp-surface-live lp-ide-chrome-wrap lp-rules-mock-wrap lp-hero-mock-body">
              <LandingRulesMock />
            </div>
            <HeroStaticImage figKey="fig03" alt="Agent rules per role" />
          </Card>
        </div>
      </div>
    </section>
  );
}
